/* ══════════════════════════════════════════════════════════════
   PWAInstall.ts — Progressive Web App Install Prompt
   Custom install banner for mobile & desktop users
   ══════════════════════════════════════════════════════════════ */

import { t } from '../utils/i18n';

// Extend the Window interface for the beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
    prompt(): Promise<void>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let bannerDismissed = false;

const DISMISS_KEY = 'imm-pwa-dismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

export const PWA_INSTALL_STYLES = `
/* ─── PWA Install Banner ─── */
.pwa-install-banner {
    position: fixed;
    bottom: 1.5rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.875rem 1.5rem;
    background: linear-gradient(135deg,
        rgba(255, 153, 51, 0.15) 0%,
        rgba(19, 136, 8, 0.15) 50%,
        rgba(6, 3, 141, 0.15) 100%);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 153, 51, 0.3);
    border-radius: 1rem;
    box-shadow:
        0 8px 32px rgba(0, 0, 0, 0.3),
        0 0 0 1px rgba(255, 255, 255, 0.05) inset;
    max-width: 480px;
    width: calc(100% - 2rem);
    animation: slideUpBanner 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    font-family: 'Inter', sans-serif;
}

@keyframes slideUpBanner {
    from {
        opacity: 0;
        transform: translateX(-50%) translateY(100%);
    }
    to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
}

.pwa-install-banner.hiding {
    animation: slideDownBanner 0.3s ease-in forwards;
}

@keyframes slideDownBanner {
    to {
        opacity: 0;
        transform: translateX(-50%) translateY(100%);
    }
}

.pwa-install-icon {
    font-size: 1.75rem;
    flex-shrink: 0;
}

.pwa-install-text {
    flex: 1;
    min-width: 0;
}

.pwa-install-title {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--text-primary, #f0f0f0);
    margin: 0 0 0.125rem 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.pwa-install-subtitle {
    font-size: 0.6875rem;
    color: var(--text-secondary, #9ca3af);
    margin: 0;
}

.pwa-install-actions {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
}

.pwa-install-btn {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.5rem;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: 'Inter', sans-serif;
}

.pwa-install-btn--primary {
    background: linear-gradient(135deg, #FF9933 0%, #138808 100%);
    color: #fff;
    box-shadow: 0 2px 8px rgba(255, 153, 51, 0.3);
}

.pwa-install-btn--primary:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(255, 153, 51, 0.4);
}

.pwa-install-btn--secondary {
    background: rgba(255, 255, 255, 0.08);
    color: var(--text-secondary, #9ca3af);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.pwa-install-btn--secondary:hover {
    background: rgba(255, 255, 255, 0.12);
    color: var(--text-primary, #f0f0f0);
}

@media (max-width: 480px) {
    .pwa-install-banner {
        bottom: 1rem;
        padding: 0.75rem 1rem;
        gap: 0.75rem;
    }

    .pwa-install-icon {
        font-size: 1.5rem;
    }
}
`;

/**
 * Render the PWA install banner (hidden by default)
 */
export function renderPWAInstallBanner(): string {
    return `
        <div id="pwa-install-banner" class="pwa-install-banner" style="display: none;">
            <span class="pwa-install-icon">📲</span>
            <div class="pwa-install-text">
                <p class="pwa-install-title">${t('pwa.installTitle', undefined) !== 'pwa.installTitle' ? t('pwa.installTitle') : 'Install India Market Monitor'}</p>
                <p class="pwa-install-subtitle">${t('pwa.installSubtitle', undefined) !== 'pwa.installSubtitle' ? t('pwa.installSubtitle') : 'Get offline access & faster performance'}</p>
            </div>
            <div class="pwa-install-actions">
                <button id="pwa-install-btn" class="pwa-install-btn pwa-install-btn--primary">
                    ${t('pwa.install', undefined) !== 'pwa.install' ? t('pwa.install') : 'Install'}
                </button>
                <button id="pwa-dismiss-btn" class="pwa-install-btn pwa-install-btn--secondary">
                    ${t('pwa.later', undefined) !== 'pwa.later' ? t('pwa.later') : 'Later'}
                </button>
            </div>
        </div>
    `;
}

/**
 * Check if banner was recently dismissed
 */
function wasDismissed(): boolean {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (!dismissed) return false;

    const timestamp = parseInt(dismissed, 10);
    if (Date.now() - timestamp < DISMISS_DURATION) return true;

    // Expired — clear
    localStorage.removeItem(DISMISS_KEY);
    return false;
}

/**
 * Show the install banner
 */
function showBanner(): void {
    if (bannerDismissed || wasDismissed()) return;

    const banner = document.getElementById('pwa-install-banner');
    if (banner) {
        banner.style.display = 'flex';
    }
}

/**
 * Hide the install banner with animation
 */
function hideBanner(): void {
    const banner = document.getElementById('pwa-install-banner');
    if (!banner) return;

    banner.classList.add('hiding');
    setTimeout(() => {
        banner.style.display = 'none';
        banner.classList.remove('hiding');
    }, 300);
}

/**
 * Initialize PWA install prompt listeners
 */
export function initPWAInstall(): void {
    // Inject styles
    const style = document.createElement('style');
    style.textContent = PWA_INSTALL_STYLES;
    document.head.appendChild(style);

    // Listen for the deferred install prompt
    window.addEventListener('beforeinstallprompt', (e: Event) => {
        e.preventDefault();
        deferredPrompt = e as BeforeInstallPromptEvent;

        // Show banner after a short delay (let page settle)
        setTimeout(() => showBanner(), 2000);
    });

    // Handle install button click
    const installBtn = document.getElementById('pwa-install-btn');
    if (installBtn) {
        installBtn.addEventListener('click', async () => {
            if (!deferredPrompt) return;

            await deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;

            if (outcome === 'accepted') {
                hideBanner();
            }

            deferredPrompt = null;
        });
    }

    // Handle dismiss button click
    const dismissBtn = document.getElementById('pwa-dismiss-btn');
    if (dismissBtn) {
        dismissBtn.addEventListener('click', () => {
            bannerDismissed = true;
            localStorage.setItem(DISMISS_KEY, Date.now().toString());
            hideBanner();
        });
    }

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
        hideBanner();
        deferredPrompt = null;
    });
}
